import React, { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { PRODUCTS } from "@/data/products";
import { whatsappLink, COMPANY } from "@/data/constants";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import WhatsappFloat from "@/components/site/WhatsappFloat";
import QuoteDialog from "@/components/site/QuoteDialog";
import SEO from "@/components/seo/SEO";
import ProductCatalogCard, { ProductCatalogGrid } from "@/components/site/ProductCatalogCard";

// Static motion props declared once — stable references avoid unnecessary re-renders.
const IMAGE_INITIAL = { opacity: 0, y: 18 };
const IMAGE_ANIMATE = { opacity: 1, y: 0 };
const IMAGE_TRANSITION = { duration: 0.6 };
const INFO_TRANSITION = { duration: 0.6, delay: 0.1 };

const splitLabel = (label) => {
  const parts = label.split(" — ");
  return parts.length >= 2
    ? { title: parts[0].trim(), spec: parts.slice(1).join(" — ").trim() }
    : { title: label, spec: "" };
};

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const product = PRODUCTS.find((p) => p.slug === slug);
  const [open, setOpen] = useState(false);
  const [defaultProductName, setDefaultProductName] = useState(undefined);

  const openQuote = (name) => {
    setDefaultProductName(name || product?.name);
    setOpen(true);
  };

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-5 text-center">
        <h1 className="font-serif-display font-extrabold text-3xl text-[var(--mn-black)]">Produto não encontrado</h1>
        <p className="text-[var(--mn-dark)] mt-3">O item solicitado não está disponível.</p>
        <button onClick={() => navigate("/")} className="btn-mn-primary mt-7">Voltar para o início</button>
      </div>
    );
  }

  const related = PRODUCTS.filter((p) => p.slug !== product.slug).slice(0, 3);
  const url = `/produtos/${product.slug}`;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: `${product.name} ${product.short}`,
    description: product.description,
    image: product.image,
    brand: { "@type": "Brand", name: COMPANY.full },
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "BRL",
      lowPrice: "0",
      offerCount: 1,
      availability: "https://schema.org/InStock",
      seller: { "@type": "Organization", name: COMPANY.full },
    },
  };

  return (
    <div className="App" data-testid="product-detail-page">
      <SEO
        title={`${product.name} ${product.short}`}
        description={product.description}
        keywords={`${product.name.toLowerCase()}, ${product.slug}, brindes corporativos, brindes personalizados`}
        image={product.image}
        url={url}
        type="product"
        jsonLd={jsonLd}
      />
      <Header onQuoteClick={() => setOpen(true)} />

      <main className="pt-[120px] lg:pt-[180px] pb-16 bg-white">
        <div className="mn-container">
          {/* Breadcrumb */}
          <nav className="text-xs uppercase tracking-[0.2em] text-[var(--mn-dark)] flex items-center gap-2 mb-7" aria-label="breadcrumb" data-testid="product-breadcrumb">
            <Link to="/" className="hover:text-[var(--mn-gold)]">Home</Link>
            <ChevronRight size={12} />
            <Link to="/#produtos" className="hover:text-[var(--mn-gold)]">Produtos</Link>
            <ChevronRight size={12} />
            <span className="text-[var(--mn-gold)]">{product.name}</span>
          </nav>

          <div className="grid lg:grid-cols-12 gap-12 items-start">
            {/* Hero Image — #F5F5F5, contain, never crop */}
            <motion.div
              initial={IMAGE_INITIAL}
              animate={IMAGE_ANIMATE}
              transition={IMAGE_TRANSITION}
              className="lg:col-span-7"
            >
              <div
                className="rounded-2xl flex items-center justify-center overflow-hidden w-full max-w-[640px]"
                style={{ background: "#F5F5F5", padding: "40px", aspectRatio: "1 / 1" }}
                data-testid="product-main-image"
              >
                <img
                  src={product.image}
                  alt={`${product.name} ${product.short}`}
                  className="max-w-full max-h-full w-auto h-auto object-contain"
                  style={{ maxWidth: "100%", maxHeight: "100%" }}
                  loading="eager"
                />
              </div>
            </motion.div>

            {/* Info */}
            <motion.div
              initial={IMAGE_INITIAL}
              animate={IMAGE_ANIMATE}
              transition={INFO_TRANSITION}
              className="lg:col-span-5"
            >
              {product.badge && (
                <span className="inline-flex items-center px-3 py-1 bg-[var(--mn-gold)] text-white text-[0.65rem] uppercase tracking-[0.22em] font-bold rounded-sm">
                  {product.badge}
                </span>
              )}
              <h1 className="mn-h-display text-[2.25rem] sm:text-[2.75rem] mt-5 leading-[1.05]" data-testid="product-title">
                {product.name}
                <span className="block text-[var(--mn-gold)] text-2xl font-medium mt-2">{product.short}</span>
              </h1>

              <p className="mt-5 text-base text-[var(--mn-dark)] leading-relaxed">{product.description}</p>

              <ul className="mt-7 space-y-3" data-testid="product-benefits">
                {product.benefits.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm text-[var(--mn-black)]">
                    <i className="fa-solid fa-check mt-1 text-[var(--mn-gold)]" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8 border-t border-[var(--mn-line)] pt-6" data-testid="product-specs">
                <h2 className="font-serif-display font-bold text-lg text-[var(--mn-black)] mb-4">Especificações</h2>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6 text-sm">
                  {Object.entries(product.specs).map(([k, v]) => (
                    <div key={k}>
                      <dt className="text-[0.65rem] uppercase tracking-[0.2em] text-[var(--mn-dark)]">{k}</dt>
                      <dd className="mt-1 font-medium text-[var(--mn-black)]">{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <button type="button" onClick={() => openQuote()} className="btn-mn-primary" data-testid="product-cta-quote">
                  <i className="fa-regular fa-paper-plane" /> Solicitar orçamento
                </button>
                <a href={whatsappLink(encodeURIComponent(`Olá! Tenho interesse no produto: ${product.name} ${product.short}.`))} target="_blank" rel="noopener noreferrer" className="btn-mn-whatsapp" data-testid="product-cta-whatsapp">
                  <i className="fa-brands fa-whatsapp" /> WhatsApp
                </a>
              </div>

              <Link to="/" className="inline-flex items-center gap-2 mt-8 text-xs uppercase tracking-[0.22em] font-medium text-[var(--mn-dark)] hover:text-[var(--mn-gold)]" data-testid="product-back-link">
                <ArrowLeft size={14} /> Voltar ao catálogo
              </Link>
            </motion.div>
          </div>

          {/* Aplicações reais — catalog grid 4/2/1 */}
          {product.gallery && product.gallery.length > 0 && (
            <section className="mt-24" data-testid="product-gallery">
              <header className="grid lg:grid-cols-12 gap-10 items-end mb-12">
                <div className="lg:col-span-7">
                  <span className="mn-eyebrow">Aplicações reais</span>
                  <h2 className="mn-h-display text-[1.75rem] sm:text-[2.25rem] lg:text-[2.5rem] mt-4 leading-tight">
                    Trabalhos entregues para <span style={{ color: "var(--mn-gold)" }}>clientes reais.</span>
                  </h2>
                </div>
                <div className="lg:col-span-5">
                  <p className="text-[var(--mn-dark)] leading-relaxed text-sm">
                    Cada peça abaixo é um projeto real entregue pela M NAIEF. Use como referência para visualizar sua marca em uma sacola de algodão personalizada.
                  </p>
                </div>
              </header>

              <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-7 auto-rows-fr place-items-stretch"
                data-testid="applications-grid"
              >
                {product.gallery.map((g, i) => {
                  const { title: labelTitle, spec } = splitLabel(g.label);
                  const specText = spec || labelTitle || "Aplicação real";
                  const material = product.specs?.Material || "Personalização sob medida";
                  return (
                    <ProductCatalogCard
                      key={g.src}
                      name={product.name}
                      code={`MN-APP-${String(i + 1).padStart(2, "0")}`}
                      image={g.src}
                      features={[
                        specText,
                        material,
                        "Pedido mínimo: 50 un.",
                      ]}
                      link={`/produtos/${product.slug}`}
                      onQuote={() => openQuote(product.name)}
                      testIdSlug={`app-${i}`}
                    />
                  );
                })}
              </div>
            </section>
          )}

          {/* Related */}
          <section className="mt-24">
            <span className="mn-eyebrow">Mais opções</span>
            <h2 className="mn-h-display text-[1.75rem] sm:text-[2rem] mt-3">
              Outros produtos <span style={{ color: "var(--mn-gold)" }}>que você pode gostar.</span>
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8" data-testid="product-related-grid">
              {related.map((p) => (
                <Link key={p.slug} to={`/produtos/${p.slug}`} className="mn-card block" data-testid={`related-${p.slug}`}>
                  <div className="mn-card-img">
                    <img src={p.image} alt={p.name} loading="lazy" />
                  </div>
                  <div className="mn-card-body">
                    <h3 className="font-serif-display font-bold text-lg text-[var(--mn-black)]">
                      {p.name}
                      <span className="block text-sm text-[var(--mn-gold)] font-medium mt-1">{p.short}</span>
                    </h3>
                    <p className="mt-3 text-sm text-[var(--mn-dark)] line-clamp-2 leading-relaxed">{p.tagline}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        </div>
      </main>

      <Footer />
      <WhatsappFloat />
      <QuoteDialog open={open} onOpenChange={setOpen} defaultProduct={defaultProductName || product.name} />
    </div>
  );
}
